/**
 * PasswordPolicyPanel — Owner / Admin / Platform-root only. Calm
 * read+write surface for the per-tenant password policy. Default state
 * is OFF; toggling controls augments — never weakens — the platform
 * baseline (10 chars, mixed case, 2 digits, symbol, no 4-in-a-row).
 *
 * Failure mode: if the endpoint 401/403s the panel renders nothing.
 * Backend remains authoritative on every write.
 */
import React, { useEffect, useState } from 'react';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'password_expiry_enabled', label: 'Force password expiry',
    help: 'Members must rotate their password after a maximum age.', type: 'bool' },
  { key: 'password_expiry_days', label: 'Expiry window (days)',
    help: 'Maximum age before rotation is required. Default 90.',
    type: 'int', min: 1, max: 365, dependsOn: 'password_expiry_enabled' },
  { key: 'require_rotation_on_next_login', label: 'Require rotation on next login',
    help: 'Every active member is asked to set a new password the next time they sign in.',
    type: 'bool' },
  { key: 'min_password_length', label: 'Minimum password length (extra)',
    help: 'Adds a stricter minimum on top of the platform 10-character baseline.',
    type: 'int_or_null', min: 8, max: 128 },
  { key: 'require_uppercase', label: 'Require an uppercase letter', type: 'bool' },
  { key: 'require_lowercase', label: 'Require a lowercase letter', type: 'bool' },
  { key: 'require_number', label: 'Require a number', type: 'bool' },
  { key: 'require_symbol', label: 'Require a symbol', type: 'bool' },
  { key: 'prevent_password_reuse_count', label: 'Prevent password reuse (last N)',
    help: 'Block a member from reusing their last N passwords. 0 = no history check. Stored as bcrypt hashes only.',
    type: 'int', min: 0, max: 24 },
];

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{ accentColor: '#475569' }}
      />
    </label>
  );
}

export default function PasswordPolicyPanel() {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [allowed, setAllowed] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenants/current/password-policy`,
        { credentials: 'include' });
      if (res.status === 401 || res.status === 403) { setAllowed(false); return; }
      if (!res.ok) { setError('Could not load policy'); return; }
      const body = await res.json();
      setPolicy(body.policy);
      setDraft(body.policy);
      setAllowed(true);
      setError(null);
    } catch {
      setError('Could not load policy');
    }
  };

  useEffect(() => { load(); }, []);

  if (!allowed || !policy || !draft) return null;

  const dirty = JSON.stringify(policy) !== JSON.stringify(draft);

  const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tenants/current/password-policy`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, reason: 'admin update' }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(formatBackendDetail(body.detail) || 'Save failed');
      } else {
        setPolicy(body.policy);
        setDraft(body.policy);
        setSavedFlash(body.changed_fields?.length
          ? `Saved (${body.changed_fields.length} change${body.changed_fields.length === 1 ? '' : 's'})`
          : 'No changes');
        setTimeout(() => setSavedFlash(null), 3000);
      }
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const enabled = !!draft.enabled;
  const headerTone = policy.enabled ? '#15803d' : '#475569';
  const headerLabel = policy.enabled ? 'ENFORCED' : 'OFF';

  return (
    <section
      style={{
        marginBottom: 24,
        border: '1px solid var(--border-subtle, #e2e8f0)',
        borderRadius: 8,
        padding: 16,
        background: 'var(--bg-elevated, #fff)',
      }}
      data-testid="password-policy-panel"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
            Password policy
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Augments the platform baseline (10 chars, mixed case, 2 digits, symbol).
            Default state is off — existing members are not forced to rotate.
          </p>
        </div>
        <span
          data-testid="password-policy-status"
          style={{
            fontSize: 11, fontWeight: 600, color: '#fff', background: headerTone,
            padding: '2px 8px', borderRadius: 12, letterSpacing: '0.04em',
          }}
        >
          {headerLabel}
        </span>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: 6,
        marginBottom: enabled ? 12 : 0,
      }}>
        <Toggle
          checked={enabled}
          onChange={(v) => update('enabled', v)}
          testId="password-policy-master-toggle"
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Enable tenant password policy</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Master switch. While off, only the platform baseline applies.
          </div>
        </div>
      </div>

      {enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FIELDS.map((f) => {
            const disabled = f.dependsOn ? !draft[f.dependsOn] : false;
            return (
              <div
                key={f.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 4px',
                  borderBottom: '1px solid var(--border-subtle, #eee)',
                }}
                data-testid={`password-policy-row-${f.key}`}
              >
                <div>
                  <div style={{ fontSize: 13 }}>{f.label}</div>
                  {f.help && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.help}</div>
                  )}
                </div>
                {f.type === 'bool' && (
                  <Toggle
                    checked={!!draft[f.key]}
                    onChange={(v) => update(f.key, v)}
                    disabled={disabled}
                    testId={`password-policy-toggle-${f.key}`}
                  />
                )}
                {(f.type === 'int' || f.type === 'int_or_null') && (
                  <input
                    type="number"
                    min={f.min} max={f.max}
                    disabled={disabled}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' && f.type === 'int_or_null') update(f.key, null);
                      else if (v === '') update(f.key, 0);
                      else update(f.key, parseInt(v, 10));
                    }}
                    style={{
                      width: 84, fontSize: 13, padding: '4px 8px',
                      border: '1px solid var(--border, #cbd5e1)', borderRadius: 4,
                    }}
                    data-testid={`password-policy-input-${f.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, alignItems: 'center' }}>
        {error && (
          <span style={{ fontSize: 12, color: '#b91c1c' }} data-testid="password-policy-error">
            {typeof error === 'string' ? error : 'Save failed'}
          </span>
        )}
        {savedFlash && !error && (
          <span style={{ fontSize: 12, color: '#15803d' }} data-testid="password-policy-saved">
            {savedFlash}
          </span>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setDraft(policy)}
          disabled={!dirty || saving}
          data-testid="password-policy-discard-btn"
        >
          Discard
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="password-policy-save-btn"
        >
          {saving ? 'Saving…' : 'Save policy'}
        </button>
      </div>
    </section>
  );
}
